(() => {
  if (window.colamyApplyCouponCheckoutInitialized) return;
  window.colamyApplyCouponCheckoutInitialized = true;

  const storagePrefix = 'colamy_apply_coupon_checkout_v2:';
  const activeStorageKey = 'colamy_apply_coupon_checkout_active_v2';
  const shopRoot = window.Shopify?.routes?.root || '/';
  const observers = new WeakMap();

  const storageKey = (coupon) => {
    const scope = [
      coupon.dataset.templateSuffix || 'default',
      coupon.dataset.productId || 'product',
      coupon.dataset.sectionId || 'section',
      coupon.dataset.blockId || coupon.dataset.couponCode || 'coupon'
    ].join(':');
    return storagePrefix + encodeURIComponent(scope);
  };

  const getStoredCode = (coupon) => {
    try {
      return localStorage.getItem(coupon ? storageKey(coupon) : activeStorageKey) || '';
    } catch (_) {
      return '';
    }
  };

  const saveCode = (coupon, code) => {
    try {
      localStorage.setItem(storageKey(coupon), code);
      localStorage.setItem(activeStorageKey, code);
    } catch (_) {}
  };

  const removeCode = (coupon) => {
    try {
      const code = getStoredCode(coupon);
      localStorage.removeItem(storageKey(coupon));
      if (code && getStoredCode().toLowerCase() === code.toLowerCase()) {
        localStorage.removeItem(activeStorageKey);
      }
    } catch (_) {}
  };

  const updateCartDiscount = (code) => {
    const update = fetch(`${shopRoot}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    }).then((response) => {
      if (!response.ok) throw new Error(`Unable to apply discount: ${response.status}`);
      return response.json();
    });

    const request = code ? update.catch(() => {
      const cartUrl = `${shopRoot}cart.js`;
      const discountUrl = `${shopRoot}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`;
      return fetch(discountUrl, { credentials: 'same-origin', redirect: 'follow' }).then((response) => {
        if (!response.ok) throw new Error(`Unable to apply discount: ${response.status}`);
        return fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      }).then((response) => {
        if (!response.ok) throw new Error(`Unable to reload cart: ${response.status}`);
        return response.json();
      });
    }) : update;

    return request.then((cart) => {
      const detail = { cart, source: 'apply-coupon-checkout' };
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
      return cart;
    });
  };

  const sectionRoot = (coupon) => document.getElementById(`MainProduct-${coupon.dataset.sectionId || ''}`)
    || coupon.closest('product-info')
    || coupon.closest('.product__info-container')
    || document;

  const variantPrices = (coupon) => {
    if (coupon._couponVariantPrices) return coupon._couponVariantPrices;
    const prices = {};
    try {
      JSON.parse(coupon.querySelector('[data-coupon-variant-prices]')?.textContent || '[]')
        .forEach((variant) => { prices[String(variant.id)] = Number(variant.price); });
    } catch (error) {
      console.warn('Unable to read coupon variant prices:', error);
    }
    coupon._couponVariantPrices = prices;
    return prices;
  };

  const selectedVariantId = (coupon) => {
    const form = document.getElementById(`product-form-${coupon.dataset.sectionId || ''}`);
    const input = form?.querySelector('input[name="id"]')
      || sectionRoot(coupon).querySelector('form[action*="/cart/add"] input[name="id"]');
    if (input?.value) return String(input.value);
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    return urlVariant || Object.keys(variantPrices(coupon))[0] || '';
  };

  const offerFor = (coupon) => {
    const price = Number(variantPrices(coupon)[selectedVariantId(coupon)]);
    const minimum = Number(coupon.dataset.minimumVariantPrice || 0);
    const percent = Number(coupon.dataset.discountPercent || 0);
    if (!Number.isFinite(price) || !percent || price < minimum) return null;
    return { coupon, price: Math.round(price * (100 - percent) / 100), percent };
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en-GB', {
        style: 'currency', currency: currency || 'GBP'
      }).format(cents / 100);
    } catch (_) {
      return `£${(cents / 100).toFixed(2)}`;
    }
  };

  const renderPrice = (coupon) => {
    if (!coupon.classList.contains('is-applied')) return;
    const offer = offerFor(coupon);
    if (!offer) return;
    const message = `${coupon.dataset.appliedText || 'Coupon applied! Final Price:'} ${formatMoney(offer.price, coupon.dataset.currencyCode)}`;
    const label = coupon.querySelector('[data-coupon-label]');
    const status = coupon.querySelector('[data-coupon-status]');
    if (label) label.textContent = message;
    if (status) status.textContent = message;
  };

  const setState = (coupon, applied) => {
    coupon.classList.toggle('is-applied', applied);
    coupon.querySelector('[data-coupon-apply-button]')?.setAttribute('aria-pressed', applied ? 'true' : 'false');
    const label = coupon.querySelector('[data-coupon-label]');
    const status = coupon.querySelector('[data-coupon-status]');
    if (label) label.textContent = applied ? coupon.dataset.appliedText : coupon.dataset.applyText;
    if (status) status.textContent = applied ? coupon.dataset.appliedText : '';
    if (applied) requestAnimationFrame(() => renderPrice(coupon));
  };

  const updateVisibleCoupon = (coupon) => {
    const coupons = [...sectionRoot(coupon).querySelectorAll('[data-apply-coupon-checkout]')];
    let best = null;
    coupons.forEach((element) => {
      const offer = offerFor(element);
      if (offer && (!best || offer.price < best.price || (offer.price === best.price && offer.percent > best.percent))) best = offer;
    });
    let active = coupons.find((element) => element.classList.contains('is-applied'));
    if (active && !offerFor(active)) {
      removeCode(active);
      setState(active, false);
      updateCartDiscount('').catch((error) => console.warn('Unable to remove coupon:', error));
      active = null;
    }
    const visible = active || best?.coupon;
    coupons.forEach((element) => { element.hidden = element !== visible; });
    if (active) renderPrice(active);
  };

  const scheduleUpdate = (coupon) => {
    clearTimeout(coupon._couponUpdateTimer);
    coupon._couponUpdateTimer = setTimeout(() => {
      updateVisibleCoupon(coupon);
      renderPrice(coupon);
    }, 80);
  };

  const initialise = () => {
    document.querySelectorAll('[data-apply-coupon-checkout]').forEach((coupon) => {
      if (coupon.dataset.couponInitialised === 'true') return;
      coupon.dataset.couponInitialised = 'true';
      variantPrices(coupon);
      const code = coupon.dataset.couponCode || '';
      if (code && getStoredCode(coupon).toLowerCase() === code.toLowerCase()) setState(coupon, true);

      const root = sectionRoot(coupon);
      root.addEventListener('change', () => scheduleUpdate(coupon), true);
      const price = document.getElementById(`price-${coupon.dataset.sectionId || ''}`);
      if (price && !observers.has(coupon)) {
        const observer = new MutationObserver(() => scheduleUpdate(coupon));
        observer.observe(price, { childList: true, subtree: true });
        observers.set(coupon, observer);
      }
      updateVisibleCoupon(coupon);
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-coupon-apply-button]');
    if (!button || button.dataset.discountProcessing === 'true') return;
    const coupon = button.closest('[data-apply-coupon-checkout]');
    const code = coupon?.dataset.couponCode?.trim();
    if (!coupon || !code) return;
    event.preventDefault();
    const applied = coupon.classList.contains('is-applied');
    button.dataset.discountProcessing = 'true';
    button.disabled = true;
    coupon.classList.add('is-loading');
    updateCartDiscount(applied ? '' : code).then(() => {
      if (applied) removeCode(coupon); else saveCode(coupon, code);
      sectionRoot(coupon).querySelectorAll('[data-apply-coupon-checkout]').forEach((element) => {
        const isCurrent = !applied && (element.dataset.couponCode || '').toLowerCase() === code.toLowerCase();
        if (!isCurrent) removeCode(element);
        setState(element, isCurrent);
      });
      updateVisibleCoupon(coupon);
      if (applied) coupon.querySelector('[data-coupon-status]').textContent = coupon.dataset.removedText || 'Coupon removed';
    }).catch((error) => {
      console.warn('Unable to apply coupon:', error);
      const label = coupon.querySelector('[data-coupon-label]');
      const message = coupon.dataset.errorText || 'Unable to apply coupon. Try again.';
      if (label) {
        label.textContent = message;
        setTimeout(() => { label.textContent = applied ? coupon.dataset.appliedText : coupon.dataset.applyText; }, 2200);
      }
      const status = coupon.querySelector('[data-coupon-status]');
      if (status) status.textContent = message;
    }).finally(() => {
      button.disabled = false;
      button.dataset.discountProcessing = 'false';
      coupon.classList.remove('is-loading');
    });
  }, true);

  ['variant:change', 'product:variant-change', 'product:variant-changed', 'shopify:section:load'].forEach((eventName) => {
    document.addEventListener(eventName, initialise);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise);
  else initialise();
})();
